/**
 * WCAG 2.x contrast helpers. Pure functions, no DOM access, shared by the
 * admin style editor (warnings) and the frontend (runtime correction).
 */

export interface Rgba {
	r: number;
	g: number;
	b: number;
	a: number;
}

type Rgb = Pick< Rgba, 'r' | 'g' | 'b' >;

const HEX_RE = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const NUMBER = '[+-]?(?:\\d+\\.?\\d*|\\.\\d+)';
const CHANNEL = `${ NUMBER }%?`;
const RGB_RE = new RegExp(
	`^rgba?\\(\\s*(${ CHANNEL })\\s*(?:,\\s*|\\s+)(${ CHANNEL })\\s*(?:,\\s*|\\s+)(${ CHANNEL })\\s*(?:(?:,|/)\\s*(${ CHANNEL })\\s*)?\\)$`,
	'i'
);

function clamp( value: number, min: number, max: number ): number {
	return Math.min( max, Math.max( min, value ) );
}

function parseChannel( raw: string ): number {
	const n = parseFloat( raw );
	return clamp( raw.endsWith( '%' ) ? ( n / 100 ) * 255 : n, 0, 255 );
}

/**
 * Parse `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa` and `rgb()`/`rgba()` (comma or
 * space syntax). Everything else (named colours, `transparent`, `var()`,
 * `color-mix()`, `hsl()`, gradients, empty, non-strings) returns `null`.
 *
 * @param value Candidate CSS colour.
 */
export function parseColor( value: unknown ): Rgba | null {
	if ( typeof value !== 'string' ) {
		return null;
	}
	const v = value.trim();

	if ( HEX_RE.test( v ) ) {
		let hex = v.slice( 1 );
		if ( hex.length <= 4 ) {
			hex = hex
				.split( '' )
				.map( ( c ) => c + c )
				.join( '' );
		}
		return {
			r: parseInt( hex.slice( 0, 2 ), 16 ),
			g: parseInt( hex.slice( 2, 4 ), 16 ),
			b: parseInt( hex.slice( 4, 6 ), 16 ),
			a: hex.length === 8 ? parseInt( hex.slice( 6, 8 ), 16 ) / 255 : 1,
		};
	}

	const match = v.match( RGB_RE );
	if ( ! match ) {
		return null;
	}
	const alphaRaw = match[ 4 ];
	let a = 1;
	if ( alphaRaw !== undefined ) {
		const n = parseFloat( alphaRaw );
		a = clamp( alphaRaw.endsWith( '%' ) ? n / 100 : n, 0, 1 );
	}
	return {
		r: parseChannel( match[ 1 ] as string ),
		g: parseChannel( match[ 2 ] as string ),
		b: parseChannel( match[ 3 ] as string ),
		a,
	};
}

function linearise( channel: number ): number {
	const s = channel / 255;
	return s <= 0.03928 ? s / 12.92 : Math.pow( ( s + 0.055 ) / 1.055, 2.4 );
}

/**
 * WCAG relative luminance of an sRGB colour.
 *
 * @param rgb Colour with 0-255 channels.
 */
export function relativeLuminance( rgb: Rgb ): number {
	return (
		0.2126 * linearise( rgb.r ) +
		0.7152 * linearise( rgb.g ) +
		0.0722 * linearise( rgb.b )
	);
}

/**
 * WCAG contrast ratio (1 to 21), independent of the argument order.
 *
 * @param a First colour.
 * @param b Second colour.
 */
export function contrastRatio( a: Rgb, b: Rgb ): number {
	const la = relativeLuminance( a );
	const lb = relativeLuminance( b );
	return ( Math.max( la, lb ) + 0.05 ) / ( Math.min( la, lb ) + 0.05 );
}

/**
 * Whether text of this colour on this background meets WCAG AA.
 *
 * @param fg    Text colour.
 * @param bg    Background colour.
 * @param large Large text only needs 3:1.
 */
export function meetsAA( fg: Rgb, bg: Rgb, large = false ): boolean {
	return contrastRatio( fg, bg ) >= ( large ? 3 : 4.5 );
}

/**
 * Black or white, whichever reads better on the background (white on a tie).
 * Every opaque background reaches at least 4.58:1 with the better of the two.
 *
 * @param bg Background colour.
 */
export function pickReadableText( bg: Rgb ): '#000000' | '#ffffff' {
	const white = contrastRatio( { r: 255, g: 255, b: 255 }, bg );
	const black = contrastRatio( { r: 0, g: 0, b: 0 }, bg );
	return white >= black ? '#ffffff' : '#000000';
}

export interface ContrastPair {
	/** Token holding the text (or ring) colour. */
	fg: string;
	/** Token that replaces `fg` when it is unset. */
	fgFallback?: string;
	/** Token holding the background colour. */
	bg: string;
	/** Required ratio: 4.5 for text, 3 for non-text (focus ring). */
	min: 4.5 | 3;
	/** The chat replaces `fg` by black or white at runtime when this pair fails. */
	autoCorrect: boolean;
}

export const CONTRAST_PAIRS: readonly ContrastPair[] = [
	{
		fg: '--chat-message-user-fg',
		bg: '--chat-message-user-bg',
		min: 4.5,
		autoCorrect: true,
	},
	{
		fg: '--chat-composer-submit-fg',
		bg: '--chat-composer-submit-bg',
		min: 4.5,
		autoCorrect: true,
	},
	{
		fg: '--chat-error-foreground',
		bg: '--chat-error',
		min: 4.5,
		autoCorrect: true,
	},
	{
		fg: '--chat-foreground',
		bg: '--chat-background',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--chat-message-assistant-fg',
		bg: '--chat-background',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--chat-message-assistant-fg',
		bg: '--chat-message-assistant-bg',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--chat-muted-foreground',
		bg: '--chat-background',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--chat-composer-fg',
		bg: '--chat-composer-bg',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--chat-composer-placeholder',
		bg: '--chat-composer-bg',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--launcher-foreground',
		bg: '--launcher-background',
		min: 4.5,
		autoCorrect: false,
	},
	{
		fg: '--chat-focus-ring',
		fgFallback: '--chat-foreground',
		bg: '--chat-background',
		min: 3,
		autoCorrect: false,
	},
	{
		fg: '--chat-focus-ring',
		fgFallback: '--chat-foreground',
		bg: '--chat-message-assistant-bg',
		min: 3,
		autoCorrect: false,
	},
	{
		fg: '--chat-focus-ring',
		fgFallback: '--chat-foreground',
		bg: '--chat-composer-bg',
		min: 3,
		autoCorrect: false,
	},
];

export interface PairResult {
	/**
	 * `unknown`: a colour is empty, transparent, translucent or not an
	 * `#hex`/`rgb()` value, so the pair is neither warned about nor corrected.
	 */
	status: 'pass' | 'fail' | 'unknown';
	/** Measured ratio (`pass` and `fail` only). */
	ratio?: number;
	/** Tokens whose non-empty value could not be evaluated (`unknown` only). */
	unknownKeys: string[];
}

function resolveToken(
	pair: ContrastPair,
	token: 'fg' | 'bg',
	values: Record< string, string >
): { key: string; value: string } {
	if ( token === 'bg' ) {
		return { key: pair.bg, value: values[ pair.bg ] ?? '' };
	}
	const own = values[ pair.fg ];
	if ( pair.fgFallback && ( own === undefined || own.trim() === '' ) ) {
		return { key: pair.fgFallback, value: values[ pair.fgFallback ] ?? '' };
	}
	return { key: pair.fg, value: own ?? '' };
}

/**
 * Evaluate one pair against a (merged) token map.
 *
 * @param pair   Pair definition.
 * @param values Token name to CSS value.
 */
export function checkPair(
	pair: ContrastPair,
	values: Record< string, string >
): PairResult {
	const fgToken = resolveToken( pair, 'fg', values );
	const bgToken = resolveToken( pair, 'bg', values );
	const fg = parseColor( fgToken.value );
	const bg = parseColor( bgToken.value );

	if ( ! fg || ! bg || fg.a < 1 || bg.a < 1 ) {
		const unknownKeys = [ fgToken, bgToken ]
			.filter( ( token ) => {
				if ( token.value.trim() === '' ) {
					return false;
				}
				const parsed = parseColor( token.value );
				return ! parsed || parsed.a < 1;
			} )
			.map( ( token ) => token.key );
		return { status: 'unknown', unknownKeys };
	}

	const ratio = contrastRatio( fg, bg );
	return {
		status: ratio >= pair.min ? 'pass' : 'fail',
		ratio,
		unknownKeys: [],
	};
}
